import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, Archive, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function fmtDate(s) {
  if (!s) return "—";
  // SQL timestamp like "2025-06-12 18:20:29"
  const d = new Date(s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ value }) {
  const map = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    blocked: "bg-amber-100 text-amber-800 border-amber-200",
    pending: "bg-blue-100 text-blue-800 border-blue-200",
    deleted: "bg-[#F50E02]/10 text-[#F50E02] border-[#F50E02]/20",
  };
  const cls = map[value] || "bg-[#F5F5F5] text-[#525252] border-black/10";
  return (
    <span
      className={`inline-flex items-center px-2 h-6 rounded-full border text-[11px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {value || "—"}
    </span>
  );
}

const PAGE_SIZE = 50;

export default function OldVehicles() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async (pageN = page, query = appliedQ) => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/old-vehicles", {
        params: { page: pageN, page_size: PAGE_SIZE, q: query || undefined },
      });
      setData(resp);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load old vehicles"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = () => {
    setPage(1);
    setAppliedQ(q.trim());
    load(1, q.trim());
  };

  const onClearSearch = () => {
    setQ("");
    setAppliedQ("");
    setPage(1);
    load(1, "");
  };

  const goTo = (p) => {
    if (p < 1 || p > (data.pages || 1)) return;
    setPage(p);
    load(p, appliedQ);
  };

  const startIdx = useMemo(
    () => (data.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1),
    [page, data.total]
  );
  const endIdx = useMemo(
    () => Math.min(page * PAGE_SIZE, data.total),
    [page, data.total]
  );

  return (
    <div data-testid="old-vehicles-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            Old Vehicles
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            Read-only legacy vehicle data imported from the previous system.
          </p>
        </div>
        <div className="text-xs text-[#525252] bg-[#FAFAFA] border border-black/5 rounded-md px-3 py-1.5">
          Total <span className="font-semibold text-[#0A0A0A]">{data.total}</span>
        </div>
      </header>

      <div className="p-8">
        <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
          {/* Search bar */}
          <div className="flex items-center gap-2 px-5 py-3 border-b border-black/5 bg-[#FAFAFA]/50">
            <div className="relative flex-1 max-w-lg">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                placeholder="Search by vehicle number, owner, phone, QR or temp number…"
                className="h-10 pl-9 pr-9 bg-white border-black/10"
                data-testid="old-vehicles-search"
              />
              {q ? (
                <button
                  type="button"
                  onClick={onClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-[#A3A3A3] hover:text-[#F50E02]"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <Button
              onClick={onSearch}
              disabled={loading}
              className="h-10 bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="old-vehicles-search-btn"
            >
              Search
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="old-vehicles-table">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">#</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Created</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Temp No.</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Vehicle No.</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Owner</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Wheeler</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Brand</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Category</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Added By</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center">
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                    </td>
                  </tr>
                ) : data.items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-[#525252]">
                      <div className="inline-flex flex-col items-center gap-2">
                        <Archive className="h-6 w-6 text-[#A3A3A3]" />
                        <span>
                          {appliedQ
                            ? `No vehicles match "${appliedQ}".`
                            : "No old vehicles found."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.items.map((v, idx) => (
                    <tr
                      key={v.id}
                      onClick={() => setDetail(v)}
                      className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60 align-top cursor-pointer"
                      data-testid={`old-vehicle-row-${v.legacy_id}`}
                    >
                      <td className="px-4 py-3 text-[#525252]">
                        {startIdx + idx}
                        <div className="text-[10px] text-[#A3A3A3]">id {v.legacy_id}</div>
                      </td>
                      <td className="px-4 py-3 text-[#0A0A0A] whitespace-nowrap">
                        {fmtDate(v.created_at)}
                      </td>
                      <td className="px-4 py-3 text-[#0A0A0A] font-mono text-xs">
                        {v.temp_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#0A0A0A] font-medium">
                        {v.vehicle_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-[#0A0A0A]">
                        <div className="font-medium">{v.owner_name || "—"}</div>
                        <div className="text-xs text-[#525252]">{v.owner_phone || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{v.wheeler_name || "—"}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{v.vehicle_brand_name || "—"}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">{v.vehicle_category_name || "—"}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]">
                        <div className="font-medium">{v.added_by_name || "—"}</div>
                        <div className="text-xs text-[#525252]">{v.added_by_email || ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge value={v.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-black/5 bg-[#FAFAFA]/30">
              <span className="text-xs text-[#525252]">
                Showing <span className="font-semibold text-[#0A0A0A]">{startIdx}-{endIdx}</span>
                {" "}of{" "}
                <span className="font-semibold text-[#0A0A0A]">{data.total}</span>
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-black/10"
                  onClick={() => goTo(page - 1)}
                  disabled={page <= 1 || loading}
                  data-testid="old-vehicles-prev"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                </Button>
                <span className="text-xs text-[#525252]">
                  Page {page} / {data.pages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-black/10"
                  onClick={() => goTo(page + 1)}
                  disabled={page >= (data.pages || 1) || loading}
                  data-testid="old-vehicles-next"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">
              {detail?.vehicle_number || "Old Vehicle"}{" "}
              <span className="text-[#A3A3A3] text-xs font-normal">
                · id {detail?.legacy_id}
              </span>
            </DialogTitle>
          </DialogHeader>
          {detail ? <OldVehicleDetail v={detail} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
        {label}
      </div>
      <div className="text-sm text-[#0A0A0A] break-words">
        {value || <span className="text-[#A3A3A3]">—</span>}
      </div>
    </div>
  );
}

function OldVehicleDetail({ v }) {
  const [cylinders, setCylinders] = useState(null); // null = loading, [] = none
  const [cylError, setCylError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCylinders(null);
      setCylError(null);
      try {
        const { data } = await api.get(`/old-vehicles/${v.legacy_id}/cylinders`);
        if (!cancelled) setCylinders(data || []);
      } catch (e) {
        if (!cancelled) {
          setCylinders([]);
          setCylError(
            formatApiErrorDetail(e?.response?.data?.detail) ||
              "Failed to load cylinders"
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [v.legacy_id]);

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
      <Field label="Temp Number" value={v.temp_number} />
      <Field label="QR Code" value={v.qrcode} />
      <Field label="Vehicle Number" value={v.vehicle_number} />
      <Field label="Vehicle Category" value={v.vehicle_category_name || v.vehicle_category} />
      <Field label="Wheeler" value={v.wheeler_name} />
      <Field label="Brand" value={v.vehicle_brand_name} />
      <Field label="Engine Stroke Type" value={v.engine_stroke_type_name || v.engine_stroke_type_id} />
      <Field label="Other Engine Stroke" value={v.other_engine_stroke_type} />
      <Field label="Engine Number" value={v.engine_number} />
      <Field label="Chassis Number" value={v.chassis_number} />
      <Field label="Manufacturing Date" value={v.vehicle_manufacturing_date} />
      <Field label="Permit Number" value={v.permit_number} />

      <div className="col-span-2 mt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-1">
          Kit
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Installation By" value={v.kit_installation_by} />
          <Field label="Installed Date" value={v.kit_installed_date} />
          <Field label="Serial Number" value={v.kit_serial_number} />
          <Field label="Manufacturer" value={v.kit_manufacturer_name} />
        </div>
      </div>

      <div className="col-span-2 mt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-1">
          Owner
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Name" value={v.owner_name} />
          <Field label="Phone" value={v.owner_phone} />
          <div className="col-span-2">
            <Field label="Address" value={v.owner_address} />
          </div>
        </div>
      </div>

      {/* Cylinders attached to this vehicle */}
      <div className="col-span-2 mt-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
            Cylinders
          </span>
          {cylinders !== null ? (
            <span className="inline-flex items-center px-1.5 h-4 rounded bg-[#FAFAFA] border border-black/5 text-[10px] font-semibold text-[#525252]">
              {cylinders.length}
            </span>
          ) : null}
        </div>

        {cylError ? (
          <div className="text-xs text-[#F50E02]">{cylError}</div>
        ) : cylinders === null ? (
          <div className="flex items-center gap-2 text-xs text-[#525252]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading cylinders…
          </div>
        ) : cylinders.length === 0 ? (
          <div className="text-xs text-[#525252] bg-[#FAFAFA] border border-black/5 rounded-md px-3 py-3">
            No cylinders linked to this vehicle in the legacy data.
          </div>
        ) : (
          <div className="border border-black/5 rounded-lg overflow-hidden">
            <table className="w-full text-xs" data-testid="old-vehicle-cylinders">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-3 py-2 font-semibold text-[#525252]">#</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">QR Code</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">Serial No.</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">Capacity</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">Manufacturer</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">Mfg / Exp Yr</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">Last / Due Test</th>
                  <th className="px-3 py-2 font-semibold text-[#525252]">Sticker</th>
                </tr>
              </thead>
              <tbody>
                {cylinders.map((c, idx) => (
                  <tr
                    key={c.id}
                    className="border-b border-black/5 last:border-0 align-top"
                  >
                    <td className="px-3 py-2 text-[#525252]">{idx + 1}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#0A0A0A]">
                      {c.qrcode || "—"}
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A]">
                      {c.cylinder_serial_number || "—"}
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A]">
                      {c.cylinder_capacity || "—"} kg
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A]">
                      <div>{c.cylinder_manufacturer_name || "—"}</div>
                      {c.cylinder_manufacturer_location ? (
                        <div className="text-[10px] text-[#A3A3A3]">
                          {c.cylinder_manufacturer_location}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A] whitespace-nowrap">
                      {c.cylinder_manufacturer_year || "—"}
                      {c.cylinder_expiry_year
                        ? ` / ${c.cylinder_expiry_year}`
                        : ""}
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A] whitespace-nowrap">
                      {c.last_test_date || "—"}
                      {c.due_test_date ? (
                        <div className="text-[10px] text-[#A3A3A3]">
                          due {c.due_test_date}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-[#0A0A0A] font-mono text-[10px]">
                      {[c.fuel_access_sticker_value_prefix, c.fuel_access_sticker_value_suffix]
                        .filter(Boolean)
                        .join("")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="col-span-2 mt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-1">
          Images (paths from legacy system)
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field label="RC Picture" value={v.rc_picture} />
          <Field label="Cylinder Image" value={v.cylinder_image} />
          <Field label="Vehicle Picture" value={v.vehicle_picture} />
        </div>
      </div>

      <div className="col-span-2 mt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3] mb-1">
          Meta
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Added By" value={v.added_by_name} />
          <Field label="Added By Email" value={v.added_by_email} />
          <Field label="Status" value={v.status} />
          <Field label="Created" value={fmtDate(v.created_at)} />
          <Field label="Updated" value={fmtDate(v.updated_at)} />
          <Field label="Legacy ID" value={String(v.legacy_id)} />
        </div>
      </div>
    </div>
  );
}
